import React from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHero from '@/components/PageHero';
import SEO from '@/components/SEO';
import { NEWS } from '@/data/news';
import { Button } from '@/components/ui/button';
import { Calendar, User, ExternalLink, MapPin, ArrowRight, Newspaper, CheckCircle2 } from 'lucide-react';

export default function RecentActivities() {
  const { id } = useParams();
  const article = id ? NEWS.find((n) => n.id === id) : null;

  if (article) return <ArticleView article={article} />;

  return (
    <div>
      <SEO
        title="Recent Activities"
        description="News coverage, school presentations, and community impact from the Drug and Alcohol Awareness program."
        path="/recent-activities"
      />
      <PageHero
        title="Recent Activities"
        subtitle="News coverage, school presentations, and community impact from the Drug & Alcohol Awareness program."
        image="https://images.unsplash.com/photo-1495020689067-958852a7765e?auto=format&fit=crop&w=1920&q=80"
        crumbs={['Recent Activities']}
      />

      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-2">In The News</div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0b2545]">Latest Coverage</h2>
            <div className="mt-3 h-1 w-16 bg-amber-400" />
          </div>
          <div className="text-sm text-slate-500">
            {NEWS.length} {NEWS.length === 1 ? 'article' : 'articles'}
          </div>
        </div>

        {/* Featured article */}
        {NEWS[0] && <FeaturedCard article={NEWS[0]} />}

        {/* More articles grid */}
        {NEWS.length > 1 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {NEWS.slice(1).map((n) => (
              <ArticleCard key={n.id} article={n} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 p-8 md:p-10 bg-gradient-to-br from-slate-50 to-amber-50 rounded-lg border border-amber-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-2">Bring It To Your School</div>
            <h3 className="font-serif text-2xl font-bold text-[#0b2545]">Schedule a presentation for your students.</h3>
            <p className="text-slate-600 mt-2 max-w-xl">
              Join a growing list of schools that have hosted Lt. Joe Kress and his team.
            </p>
          </div>
          <Button asChild size="lg" className="bg-[#0b2545] hover:bg-[#123a6b] text-white">
            <Link to="/contact">Contact Us <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function FeaturedCard({ article }) {
  return (
    <Link
      to={`/recent-activities/${article.id}`}
      className="group grid md:grid-cols-2 gap-0 overflow-hidden rounded-lg bg-white shadow-sm hover:shadow-xl border border-slate-200 transition-all"
    >
      <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
        <img
          src={article.cover}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-8 md:p-10 flex flex-col justify-center">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {article.tags.map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded bg-amber-100 text-amber-700">
              {t}
            </span>
          ))}
        </div>
        <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#0b2545] leading-tight mb-4 group-hover:text-amber-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-slate-600 leading-relaxed mb-5">{article.summary}</p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
          <span className="flex items-center gap-1.5"><Newspaper className="h-4 w-4 text-amber-500" /> {article.source}</span>
          <span className="flex items-center gap-1.5"><User className="h-4 w-4 text-amber-500" /> {article.author}</span>
          <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-amber-500" /> {article.date}</span>
        </div>
        <div className="mt-6 inline-flex items-center text-sm font-semibold text-[#0b2545] group-hover:text-amber-600 transition-colors">
          Read full story <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

function ArticleCard({ article }) {
  return (
    <Link
      to={`/recent-activities/${article.id}`}
      className="group block bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg border border-slate-200 transition-all"
    >
      <div className="aspect-video overflow-hidden">
        <img src={article.cover} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-6">
        <div className="text-xs text-amber-600 font-semibold uppercase tracking-widest mb-2">{article.source}</div>
        <h3 className="font-serif text-lg font-bold text-[#0b2545] leading-tight mb-2 group-hover:text-amber-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-slate-600 line-clamp-3">{article.summary}</p>
        <div className="mt-4 text-xs text-slate-500">{article.date} • {article.author}</div>
      </div>
    </Link>
  );
}

function ArticleView({ article }) {
  return (
    <div>
      <SEO
        title={article.title}
        description={article.summary}
        path={`/recent-activities/${article.id}`}
        image={article.cover}
        type="article"
      />
      <PageHero
        title={article.title}
        subtitle={article.summary}
        image={article.cover}
        crumbs={['Recent Activities', article.source]}
      />

      <article className="max-w-3xl mx-auto px-4 py-16">
        {/* Meta strip */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600 mb-8 pb-6 border-b border-slate-200">
          <span className="flex items-center gap-1.5"><Newspaper className="h-4 w-4 text-amber-500" /> {article.source}</span>
          <span className="flex items-center gap-1.5"><User className="h-4 w-4 text-amber-500" /> {article.author}</span>
          <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-amber-500" /> {article.date}</span>
          {article.location && (
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-amber-500" /> {article.location}</span>
          )}
        </div>

        {/* Body */}
        <div className="space-y-5">
          {article.paragraphs.map((p, i) => (
            <p key={i} className="text-slate-700 leading-[1.9] text-[16px]">{p}</p>
          ))}
        </div>

        {/* Highlights */}
        {article.highlights?.length > 0 && (
          <div className="mt-10 p-6 bg-slate-50 rounded-lg border-l-4 border-amber-400">
            <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-3">Key Takeaways</div>
            <ul className="space-y-2.5">
              {article.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Source link */}
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-6 bg-[#0b2545] rounded-lg text-white">
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-300 mb-1">Original Coverage</div>
            <div className="font-serif text-lg font-bold">{article.source}</div>
          </div>
          <Button asChild variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0b2545]">
            <a href={article.sourceUrl} target="_blank" rel="noopener noreferrer">
              Visit {article.source} <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>

        {/* Back link */}
        <div className="mt-10">
          <Link to="/recent-activities" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0b2545] hover:text-amber-600 transition-colors">
            ← Back to Recent Activities
          </Link>
        </div>
      </article>
    </div>
  );
}
